import React from 'react';
import { reduxForm, Field, submit, change } from 'redux-form';
import { connect } from 'react-redux';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import _ from 'lodash';
import { CUSTOM_TABLE_FIELD_TYPES, EXTERNAL_CORE_TABLE_TYPES } from '../../../_config/consts';
import CustomField from '../Fields/CustomField.jsx';
import SelectableField from '../Fields/SelectableInput/SelectableField.jsx';
import MDButton from '../../MDButton/MDButton.jsx';
import FormFlatContainer from '../Elements/FormFlatContainer.jsx';
import * as GuestActions from '../../../redux/actions/guest.actions';
import * as LockActions from '../../../redux/actions/lock.actions';
import * as CredentialActions from '../../../redux/actions/credential.actions';

const getFieldType = (type) => {
  switch (type) {
    case CUSTOM_TABLE_FIELD_TYPES.TEXT:
      return 'text';
    case CUSTOM_TABLE_FIELD_TYPES.INTEGER_NUMBER:
      return 'number';
    case CUSTOM_TABLE_FIELD_TYPES.DECIMAL_NUMBER:
      return 'number';
    case CUSTOM_TABLE_FIELD_TYPES.DATE:
      return 'airbnbDate';
    case CUSTOM_TABLE_FIELD_TYPES.MONETARY:
      return 'number';
    case CUSTOM_TABLE_FIELD_TYPES.EXTERNAL_REFERENCE:
      return 'text';
    default:
      return 'text';
  }
};

const getFieldWidth = (type) => {
  switch (type) {
    case CUSTOM_TABLE_FIELD_TYPES.INTEGER_NUMBER:
    case CUSTOM_TABLE_FIELD_TYPES.DECIMAL_NUMBER:
    case CUSTOM_TABLE_FIELD_TYPES.MONETARY:
    case CUSTOM_TABLE_FIELD_TYPES.DATE:
      return '200px';
    case CUSTOM_TABLE_FIELD_TYPES.TEXT:
    case CUSTOM_TABLE_FIELD_TYPES.EXTERNAL_REFERENCE:
    default:
      return '100%';
  }
};

const validate = (values, props) => {
  const errors = {};
  _.forEach(props.fields, (field) => {
    if (!values[field.name]) {
      errors[field.name] = 'required';
    }
  });
  return errors;
};

class CustomTableRowForm extends React.Component {
  
  constructor(props) {
    super(props);
    this.state = {
      usersOptions: [],
      locksOptions: [],
      credentialsOptions: [],
    };
  }

  async onFilterUsers(value) {
    const { dispatch } = this.props;
    try {
      const users = await dispatch(GuestActions.fetchGuestsForSelector(value));
      const options = _.map(users, user => ({ 
        value: user.id, 
        label: `${user.firstname} ${user.lastname} (${user.email})` 
      }));
      this.setState({ usersOptions: options });
      return options;
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }

  async onFilterLocks(value) {
    const { dispatch } = this.props;
    try {
      const response = await dispatch(LockActions.fetchLocks(0, false, 50));
      const locks = response || [];
      const filteredLocks = value ? 
        _.filter(locks, lock => 
          (lock.name && lock.name.toLowerCase().includes(value.toLowerCase())) ||
          (lock.deviceId && lock.deviceId.toLowerCase().includes(value.toLowerCase()))
        ) : locks;
      const options = _.map(filteredLocks, lock => ({ 
        value: lock.id, 
        label: lock.name || lock.deviceId 
      }));
      this.setState({ locksOptions: options });
      return options;
    } catch (error) {
      console.error('Error fetching locks:', error);
      return [];
    }
  }

  async onFilterCredentials(value) {
    const { dispatch } = this.props;
    try {
      const response = await dispatch(CredentialActions.fetchSmartphoneCredentialRules(0, false, 50));
      const credentials = response && response.content ? response.content : (response || []);
      const filteredCredentials = value ? 
        _.filter(credentials, credential => 
          (credential.name && credential.name.toLowerCase().includes(value.toLowerCase()))
        ) : credentials;
      const options = _.map(filteredCredentials, credential => ({ 
        value: credential.id, 
        label: credential.name 
      }));
      this.setState({ credentialsOptions: options });
      return options;
    } catch (error) {
      console.error('Error fetching credentials:', error);
      return [];
    }
  }

  onSelectEntity(field, entity) {
    // Entity is already in the format { value, label }
    // The form will store the value automatically
    const { dispatch } = this.props;
    dispatch(change('CustomTableRowForm', field.name, entity.value));
  }

  renderField(field) {
    const { usersOptions, locksOptions, credentialsOptions } = this.state;

    // Check if this is an external reference field
    if (field.type === CUSTOM_TABLE_FIELD_TYPES.EXTERNAL_REFERENCE && field.externalCoreTable) {
      switch (field.externalCoreTable) {
        case EXTERNAL_CORE_TABLE_TYPES.USER:
          return (
            <div key={field.name} style={{ width: '100%', display: 'block', marginBottom: '15px' }}>
              <Field
                name={field.name}
                component={SelectableField}
                noCache
                options={usersOptions}
                placeholder={<Entity entity="selectUser" />}
                title={field.name}
                onInputChange={value => this.onFilterUsers(value)}
                onSelect={entity => this.onSelectEntity(field, entity)}
              />
            </div>
          );
        
        case EXTERNAL_CORE_TABLE_TYPES.SMART_LOCK:
          return (
            <div key={field.name} style={{ width: '100%', display: 'block', marginBottom: '15px' }}>
              <Field
                name={field.name}
                component={SelectableField}
                noCache
                options={locksOptions}
                placeholder={<Entity entity="selectSmartLock" />}
                title={field.name}
                onInputChange={value => this.onFilterLocks(value)}
                onSelect={entity => this.onSelectEntity(field, entity)}
              />
            </div>
          );
        
        case EXTERNAL_CORE_TABLE_TYPES.CREDENTIAL:
          return (
            <div key={field.name} style={{ width: '100%', display: 'block', marginBottom: '15px' }}>
              <Field
                name={field.name}
                component={SelectableField}
                noCache
                options={credentialsOptions}
                placeholder={<Entity entity="selectCredential" />}
                title={field.name}
                onInputChange={value => this.onFilterCredentials(value)}
                onSelect={entity => this.onSelectEntity(field, entity)}
              />
            </div>
          );
        
        default:
          // Fallback to text field if externalCoreTable is not recognized
          return (
            <div key={field.name} style={{ width: getFieldWidth(field.type), display: 'block', marginBottom: '15px' }}>
              <Field
                name={field.name}
                type="text"
                component={CustomField}
                title={field.name}
              />
            </div>
          );
      }
    }

    // Default field rendering for non-external-reference fields
    return (
      <div key={field.name} style={{ width: getFieldWidth(field.type), display: 'block', marginBottom: '15px' }}>
        <Field
          name={field.name}
          type={getFieldType(field.type)}
          component={CustomField}
          title={field.name}
          placeholder={field.type === CUSTOM_TABLE_FIELD_TYPES.DATE ? '' : undefined}
        />
      </div>
    );
  }

  render() {
    const { handleSubmit, fields, dispatch } = this.props;

    return (
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <FormFlatContainer title="customTableRowValue" titleContainerStyle={{ marginBottom: 20 }}>
            {_.map(fields, (field) => this.renderField(field))}
          </FormFlatContainer>
          <div style={{ display: 'flex', width: '100%', marginTop: 20 }}>
            <MDButton
              title={<Entity entity="save" />}
              containerStyle={{ position: 'absolute', left: 0, right: 0, bottom: 0, width: '100%', margin: 0 }}
              style={{ height: 45, borderRadius: 0 }}
              onClick={() => dispatch(submit('CustomTableRowForm'))}
            />
          </div>
        </div>
      </div>
    );
  }
}

const CustomTableRowFormConnected = connect(state => ({ 
  form: state.form.CustomTableRowForm 
}))(CustomTableRowForm);

export default reduxForm({
  form: 'CustomTableRowForm',
  validate,
})(CustomTableRowFormConnected); 